import { createContext, useContext, useEffect, useState } from 'react';
import { getThemeSite } from 'services/settingService';

const SeasonThemeContext = createContext();

export function SeasonThemeProvider({ children }) {
  // Thème initial issu d'une source persistée pour éviter le "switch" visuel au premier rendu
  const persistedTheme = typeof window !== 'undefined'
    ? (localStorage.getItem('seasonTheme') || document.documentElement.getAttribute('data-season-theme'))
    : null;
  const [seasonTheme, setSeasonTheme] = useState(persistedTheme || 'default');
  const [isLoading] = useState(false); // Ne plus bloquer le rendu

  useEffect(() => {
    // Appliquer dès le montage l'attribut data selon la valeur initiale
    document.documentElement.setAttribute('data-season-theme', seasonTheme);

    // Charger le thème en arrière-plan sans bloquer le rendu
    const fetchSeasonTheme = async () => {
      try {
        const response = await getThemeSite();
        const currentTheme = response?.data?.hub_status || response?.data?.theme || 'default';
        // Ne mettre à jour que si différent du thème en cache
        setSeasonTheme(prevTheme => {
          if (currentTheme !== prevTheme) {
            document.documentElement.setAttribute('data-season-theme', currentTheme);
            try { localStorage.setItem('seasonTheme', currentTheme); } catch (_) {}
            return currentTheme;
          }
          return prevTheme;
        });
      } catch (error) {
        console.error('Erreur lors du chargement du thème saisonnier:', error);
        // En cas d'erreur, garder le thème en cache
      }
    };

    // Charger en arrière-plan avec un petit délai pour ne pas bloquer le rendu initial
    const timeoutId = setTimeout(fetchSeasonTheme, 100);
    return () => clearTimeout(timeoutId);
  }, [seasonTheme]);

  const updateSeasonTheme = (newTheme) => {
    setSeasonTheme(newTheme);
    document.documentElement.setAttribute('data-season-theme', newTheme);
  };

  return (
    <SeasonThemeContext.Provider value={{ 
      seasonTheme, 
      updateSeasonTheme, 
      isLoading 
    }}>
      {children}
    </SeasonThemeContext.Provider>
  );
}

export function useSeasonTheme() {
  const context = useContext(SeasonThemeContext);
  if (!context) {
    throw new Error('useSeasonTheme must be used within a SeasonThemeProvider');
  }
  return context;
}
